import { ApiResult, HttpClient } from "../http.js";

/**
 * Grant lifecycle as the API spells it (`SupplierAccessGrantStatus`). The list returns live grants only.
 * Named apart from the customer-side `SupplierGrantStatus` so the two PRs never clash.
 */
export type SupplierCustomerStatus = "Pending" | "Active" | "Revoked" | "Declined";

/** How the grant came to exist (`SupplierAccessGrantOrigin`). */
export type SupplierCustomerOrigin = "InvitedByCustomer" | "CreatedBySupplier" | "Transferred" | "RequestedBySupplier";

/** One row of GET /api/supplier/customers. A supplier admin (`supplier.customers.viewAll`) gets every salesman's rows. */
export interface SupplierCustomerDto {
  grantId: string;
  companyId: string;
  name: string;
  cvr: string | null;
  status: SupplierCustomerStatus;
  origin: SupplierCustomerOrigin;
  /** ISO timestamp of the grant. */
  since: string;
  sellerUserId: string;
  sellerName: string;
}

/** Calendar period of the dashboard, KPI and summary endpoints (UTC). */
export type SupplierPeriod = "month" | "quarter" | "year";

/** Metric keys the API lists in `restrictedMetrics` when the customer's permission set hides them. */
export type SupplierRestrictedMetric = "projects" | "quotes" | "analyses" | "purchases" | "lastActivity";

/** One customer of GET /api/supplier/dashboard (also `customer` of the summary). A restricted metric is null and listed in `restrictedMetrics`. */
export interface SupplierDashboardCustomerDto {
  grantId: string;
  companyId: string;
  name: string;
  cvr: string | null;
  sellerUserId: string;
  sellerName: string;
  origin: SupplierCustomerOrigin;
  since: string;
  subscriptionTier: string | null;
  subscriptionStatus: string | null;
  subscriptionPeriodEnd: string | null;
  /** null: no DPA text is shipped. */
  dpaAccepted: boolean | null;
  activeProjects: number | null;
  quotesSent: number | null;
  quotesWon: number | null;
  quotesWonValueExclVat: number | null;
  analysesRun: number | null;
  purchasesExclVat: number | null;
  lastActivityAt: string | null;
  priceItems: number | null;
  priceListLastSyncAt: string | null;
  /** false until the Phase 4 catalogue: hide the price-list figures. */
  priceListMetricAvailable: boolean;
  restrictedMetrics: SupplierRestrictedMetric[];
}

export interface SupplierDashboardAggregateDto {
  customerCount: number;
  activeCustomers30Days: number;
  activeProjects: number;
  quotesSent: number;
  quotesWon: number;
  quotesWonValueExclVat: number;
  analysesRun: number;
  purchasesExclVat: number;
  restrictedCustomers: Record<SupplierRestrictedMetric, number>;
  onboardedInPeriodByOrigin: Record<SupplierCustomerOrigin, number>;
}

export type SupplierFollowUpKind =
  | "analysis_without_quote"
  | "quote_not_won"
  | "price_list_stale"
  | "no_activity"
  | "dpa_pending";

export interface SupplierFollowUpDto {
  kind: SupplierFollowUpKind;
  grantId: string;
  companyId: string;
  companyName: string;
  /** Danish, server-rendered: shown as is. */
  text: string;
  subjectId: string | null;
  days: number | null;
}

/** GET /api/supplier/dashboard. `followUps` is absent on a backend older than the Følg op slice. */
export interface SupplierDashboardDto {
  period: SupplierPeriod;
  periodStart: string;
  periodEnd: string;
  aggregate: SupplierDashboardAggregateDto;
  customers: SupplierDashboardCustomerDto[];
  followUps?: SupplierFollowUpDto[];
}

export interface SupplierSummaryProjectDto {
  id: string;
  number: string | null;
  title: string;
  status: string;
  lastActivityAt: string | null;
}

export interface SupplierSummaryAnalysisDto {
  id: string;
  kind: "tender" | "drawing";
  title: string;
  status: string;
  createdAt: string;
}

/** GET /api/supplier/customers/{grantId}/summary. `projects` / `openAnalyses` are null when restricted. */
export interface SupplierCustomerSummaryDto {
  period: SupplierPeriod;
  periodStart: string;
  periodEnd: string;
  customer: SupplierDashboardCustomerDto;
  ownerName: string | null;
  ownerEmail: string | null;
  projects: SupplierSummaryProjectDto[] | null;
  openAnalyses: SupplierSummaryAnalysisDto[] | null;
}

export type SupplierKpiKey = "customersOnboarded" | "activeCustomers" | "analysesRun" | "purchasesFromSupplier";

/** GET /api/supplier/kpis (read side only). `target`/`progress` are null without a target. */
export interface SupplierKpisDto {
  period: SupplierPeriod;
  periodStart: string;
  periodEnd: string;
  scope: string;
  canEdit: boolean;
  kpis: { kpi: SupplierKpiKey; target: number | null; actual: number; progress: number | null }[];
}

/** Body of POST /api/supplier/customers. All fields required; the server validates (400 with `errors`). */
export interface CreateSupplierCustomerPayload {
  name: string;
  cvr: string;
  address: string;
  ownerEmail: string;
  ownerName: string;
  /** Plain text shown in the owner's welcome mail; trimmed, at most 1000 characters. */
  message?: string;
}

export interface CreatedSupplierCustomerDto {
  companyId: string;
  grantId: string;
  name: string;
}

export interface RequestSupplierAccessResultDto {
  grantId: string;
  customerName: string;
  status: SupplierCustomerStatus;
}

/** Body of POST /api/supplier-access/accept: the id and token from the invitation mail. */
export interface AcceptSupplierAccessPayload {
  id: string;
  token: string;
}

export interface AcceptSupplierAccessResultDto {
  grantId: string;
  customerCompanyId: string;
  supplierCompanyId: string;
}

export interface SellerCustomerTransferDto {
  grantId: string;
  newUserId: string;
}

export interface TransferSellerCustomersResultDto {
  moved: number;
}

export interface TransferSupplierGrantResultDto {
  oldGrantId: string;
  newGrantId: string;
}

/**
 * Transfer refusals: 404 `grant_not_found`; 409 `grant_not_active` | `same_holder` |
 * `new_holder_not_member` | `new_holder_has_access`.
 * Supplier side of the account-manager feature. The active org is the supplier company.
 * Refusals carry a stable `code` on the error (see ApiError.code): create 409
 * `cvr_already_registered` | `owner_is_current_user` | `owner_email_unconfirmed`; request-access
 * 404 `cvr_not_registered`, 409 `target_not_allowed` | `already_member` | `access_already_exists`;
 * accept 404 `invitation_not_found` | `invitation_already_used` | `invitation_expired`, 403
 * `email_not_verified` | `invitation_email_mismatch` | `not_a_member_of_supplier` |
 * `employer_not_on_mesterplan`, 409 `supplier_ambiguous` | `access_already_exists`.
 */
export class SupplierApi {
  constructor(private readonly http: HttpClient) {}

  listCustomers(): Promise<ApiResult<SupplierCustomerDto[]>> {
    return this.http.get<SupplierCustomerDto[]>("/api/supplier/customers");
  }

  createCustomer(payload: CreateSupplierCustomerPayload): Promise<ApiResult<CreatedSupplierCustomerDto>> {
    return this.http.post<CreatedSupplierCustomerDto>("/api/supplier/customers", { body: payload });
  }

  /** Asks an existing company for access by CVR (8 digits). Creates a Pending grant and mails its admins. */
  requestAccess(cvr: string, message?: string): Promise<ApiResult<RequestSupplierAccessResultDto>> {
    return this.http.post<RequestSupplierAccessResultDto>("/api/supplier/customers/request-access", {
      body: message ? { cvr, message } : { cvr },
    });
  }

  /** Signed-in, email-verified caller; no org context needed. */
  acceptInvitation(payload: AcceptSupplierAccessPayload): Promise<ApiResult<AcceptSupplierAccessResultDto>> {
    return this.http.post<AcceptSupplierAccessResultDto>("/api/supplier-access/accept", { body: payload });
  }

  /**
   * Drops access: the account manager's own grant, or any grant for a supplier admin.
   * 404 `grant_not_found`, 403 `not_your_grant`, 409 `grant_not_live`.
   */
  removeAccess(grantId: string): Promise<ApiResult<void>> {
    return this.http.delete<void>(`/api/supplier/customers/${grantId}`);
  }

  /** Per-customer figures, Følg op and aggregates for the caller's customers (all sellers with `supplier.customers.viewAll`). 400 `invalid` for a bad period. `anchor` (yyyy-MM-dd) selects the period containing that Copenhagen day (default today); a future day is 400 `anchor_in_future`. */
  getDashboard(period: SupplierPeriod = "month", anchor?: string): Promise<ApiResult<SupplierDashboardDto>> {
    return this.http.get<SupplierDashboardDto>("/api/supplier/dashboard", {
      query: anchor ? { period, anchor } : { period },
    });
  }

  /** One customer's key figures, projects and open analyses. 404 `grant_not_found`. */
  getCustomerSummary(
    grantId: string,
    period: SupplierPeriod = "month",
    anchor?: string,
  ): Promise<ApiResult<SupplierCustomerSummaryDto>> {
    return this.http.get<SupplierCustomerSummaryDto>(`/api/supplier/customers/${grantId}/summary`, {
      query: anchor ? { period, anchor } : { period },
    });
  }

  /** Targets and progress for the four KPIs; `scope` is `me`, `org` (admin) or `user:{id}`. */
  getKpis(period: SupplierPeriod = "month", scope = "me"): Promise<ApiResult<SupplierKpisDto>> {
    return this.http.get<SupplierKpisDto>("/api/supplier/kpis", { query: { period, scope, history: 0 } });
  }

  /** Supplier admin (`supplier.customers.manage`): moves an Active grant to another salesman of the same supplier. */
  transferCustomer(grantId: string, newUserId: string): Promise<ApiResult<TransferSupplierGrantResultDto>> {
    return this.http.post<TransferSupplierGrantResultDto>(`/api/supplier/customers/${grantId}/transfer`, {
      body: { newUserId },
    });
  }

  /**
   * Supplier admin (`supplier.customers.manage`): moves several Active grants of one salesman to colleagues,
   * all or nothing. 400 `no_transfers` / `duplicate_grant`, 404 `grant_not_found`, 409 `grant_not_held_by_seller` /
   * `grant_not_active` / `same_holder` / `new_holder_not_member` / `new_holder_has_access`.
   */
  transferSellerCustomers(
    userId: string,
    transfers: SellerCustomerTransferDto[],
  ): Promise<ApiResult<TransferSellerCustomersResultDto>> {
    return this.http.post<TransferSellerCustomersResultDto>(`/api/supplier/sellers/${userId}/transfer-customers`, {
      body: { transfers },
    });
  }
}
